import { FC } from "react"

import { useTranslation } from "react-i18next"

import { SectionCard } from "components/settings/SectionCard"

import { ProviderGuide } from "./guides"

interface ProviderGuideSectionProps {
  guide: ProviderGuide
  /** Origins this tenant needs allowlisted, already resolved from the Tenant row. */
  origins: string[]
}

/** The vendor's own snippet, where each value hides in it, and the prerequisites. */
export const ProviderGuideSection: FC<ProviderGuideSectionProps> = ({ guide, origins }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const base = `settings.liveChat.guide.${guide.provider}`
  const originList = origins.length ? origins.join(", ") : t("settings.liveChat.guide.originsUnknown")

  return (
    <SectionCard title={t(`${base}.title`)} description={t(`${base}.description`)}>
      <div className="min-w-0">
        <p className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-300">
          {t("settings.liveChat.guide.snippetLabel")}
        </p>
        <pre className="max-w-full overflow-x-auto rounded-lg border border-black/10 bg-black/5 p-3 text-xs leading-relaxed text-gray-800 dark:border-white/10 dark:bg-white/5 dark:text-gray-200">
          {guide.snippet}
        </pre>
      </div>

      <div className="min-w-0">
        <p className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-300">
          {t("settings.liveChat.guide.extractLabel")}
        </p>
        <ul className="flex flex-col gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          {guide.extract.map((key) => (
            <li key={key} className="flex gap-2">
              <span aria-hidden="true" className="text-gray-400">
                •
              </span>
              <span className="break-words">{t(`${base}.extract.${key}`)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="min-w-0 rounded-lg border border-warning/40 bg-warning/10 p-3">
        <p className="mb-2 text-sm font-medium text-warning">{t("settings.liveChat.guide.requirementsLabel")}</p>
        <ul className="flex flex-col gap-1.5 text-xs text-gray-700 dark:text-gray-200">
          {guide.requirements.map((key) => (
            <li key={key} className="flex gap-2">
              <span aria-hidden="true" className="text-warning">
                •
              </span>
              <span className="break-words">{t(`${base}.requirements.${key}`, { origins: originList })}</span>
            </li>
          ))}
        </ul>
      </div>
    </SectionCard>
  )
}
